import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { getSiteConfig } from "@/lib/api";
import { Dna, Cpu, HeartPulse, Target, Microscope, Leaf, Lightbulb, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const ICON_MAP = { Dna, Cpu, HeartPulse, Microscope, Leaf, Lightbulb, Target };

export default function ResearchSection() {
  const { ref, inView } = useInView({ threshold: 0.15, triggerOnce: true });
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    getSiteConfig("research").then(r => setCfg(r.data?.data || {})).catch(() => {});
  }, []);

  const overline = cfg?.overline || "Nos axes de recherche";
  const title = cfg?.title || "Domaines d'expertise";
  const subtitle = cfg?.subtitle || "";
  const axes = cfg?.axes || [];
  const objectives = cfg?.objectives || [];

  return (
    <section id="research" data-testid="research-section" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }} className="text-center mb-16">
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#1670B9] mb-4">{overline}</p>
          <h2 className="font-['Quicksand'] text-3xl lg:text-4xl font-medium tracking-tight text-slate-800 mb-4" data-testid="research-title">{title}</h2>
          {subtitle && <p className="text-base text-slate-500 max-w-2xl mx-auto">{subtitle}</p>}
        </motion.div>

        <div className={`grid grid-cols-1 ${axes.length <= 3 ? "md:grid-cols-3" : "md:grid-cols-2 lg:grid-cols-4"} gap-6 mb-16`}>
          {axes.map((area, i) => {
            const IconComp = ICON_MAP[area.icon] || Dna;
            return (
              <Link to={`/research/${i}`} key={`axis-${area.title}-${i}`}>
                <motion.div initial={{ opacity: 0, y: 25 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: i * 0.12 }}
                  className="group relative bg-white rounded-xl border border-slate-100 overflow-hidden hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer h-full" data-testid={`research-card-${i}`}>
                  {area.image_url && (
                    <div className="h-44 overflow-hidden relative">
                      <img src={area.image_url} alt={area.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center mb-4 border-2 border-white shadow-sm" style={area.image_url ? { marginTop: "-3rem", position: "relative", zIndex: 10 } : {}}>
                      <IconComp className="w-5 h-5" style={{ color: area.color || "#1670B9" }} />
                    </div>
                    <h3 className="font-['Quicksand'] text-base font-semibold text-slate-800 mb-3 leading-snug">{area.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed mb-4">{area.desc}</p>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#0F62B3] group-hover:gap-2 transition-all">En savoir plus <ArrowRight className="w-3.5 h-3.5" /></span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-1 opacity-0 group-hover:opacity-100 transition-opacity" style={{ backgroundColor: area.color || "#1670B9" }} />
                </motion.div>
              </Link>
            );
          })}
        </div>

        {objectives.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-gradient-to-br from-[#1670B9]/5 to-[#74CDD9]/5 rounded-xl p-8 border border-[#1670B9]/10">
            <div className="flex items-center gap-3 mb-6">
              <Target className="w-5 h-5 text-[#F49C03]" />
              <h3 className="font-['Quicksand'] text-lg font-semibold text-slate-800">Objectifs spécifiques</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {objectives.map((obj, i) => (
                <div key={`obj-${obj.slice(0,12)}-${i}`} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#1670B9]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-[#1670B9]">{i + 1}</span>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{obj}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}
